// src/pages/CustomerFollowUp.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../component/Header";
import CnSidebar from "../China/CnSidebar";
import CustomerSSidebar from "./components/CustomerSSidebar";
import Sidebar from "../../component/Sidebar";

const apiUrl = process.env.REACT_APP_API_URL;

export default function CustomerFollowUp() {
  const [userRole, setUserRole] = useState("");
  const [userName, setUserName] = useState(null);
  const userId = localStorage.getItem("token");

  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    model: "",
  });
  const [filter, setFilter] = useState({ search: "", status: "all" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${apiUrl}/summary/${selectedMonth}`);
      setSummary(res.data);
    } catch (err) {
      console.error("❌ Summary error:", err);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [selectedMonth]);

  useEffect(() => {
    setUserName(localStorage.getItem("user"));
    setUserRole(localStorage.getItem("role"));
  }, []);
  // ✅ Fetch all customers
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/customers`);
      setCustomers(
        res.data.sort((a, b) => {
          const order = { high: 1, medium: 2, low: 3 };
          return order[a.priority] - order[b.priority];
        })
      );
      setError("");
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Add new customer
  const addCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone || !newCustomer.model) return;
    try {
      await axios.post(`${apiUrl}/customers`, newCustomer);
      setNewCustomer({ name: "", phone: "", model: "" });
      fetchCustomers();
    } catch (err) {
      console.error("❌ Add error:", err);
      setError("Could not add customer");
    }
  };

  // ✅ Mark as called
  const markAsCalled = async (id) => {
    try {
      await axios.patch(`${apiUrl}/customers/${id}`, { status: "called" });
      fetchCustomers();
    } catch (err) {
      console.error("❌ Update error:", err);
      setError("Could not update status");
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filtered = customers.filter((c) => {
    const matchSearch =
      c.name.toLowerCase().includes(filter.search.toLowerCase()) ||
      c.phone.includes(filter.search) ||
      c.model.toLowerCase().includes(filter.search.toLowerCase());
    const matchStatus = filter.status === "all" || c.status === filter.status;
    return matchSearch && matchStatus;
  });

  // ✅ Update purchase status
  const updatePurchase = async (id, status) => {
    try {
      await axios.patch(`${apiUrl}/customers/${id}/purchase`, {
        purchase_status: status,
      });
      fetchCustomers();
    } catch (err) {
      console.error("❌ Purchase update error:", err);
      setError("Could not update purchase status");
    }
  };

  const updatePriority = async (id, priority) => {
    try {
      await axios.patch(`${apiUrl}/customers/${id}/priority`, { priority });
      fetchCustomers();
    } catch (err) {
      console.error("❌ Priority update error:", err);
      setError("Could not update priority");
    }
  };

  return (
    <>
      <Header />
      {userRole !== "Admin" && <CustomerSSidebar />}
      {(userRole === "Admin" || userName === "yeabkal") && <Sidebar />}
      {userName === "customerService" && <CustomerSSidebar />}

      <div className="max-w-5xl mx-auto p-6 space-y-6 mt-20">
        <h1 className="text-2xl font-bold">Customer Follow-Up</h1>

        {/* Add Customer Form */}
        <div className="bg-white p-4 rounded-xl shadow-md space-y-3 border">
          <h2 className="text-lg font-semibold">Add New Customer</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Customer Name"
              value={newCustomer.name}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, name: e.target.value })
              }
              className="border p-2 rounded-md focus:outline-blue-500"
            />
            <input
              type="text"
              placeholder="Phone Number"
              value={newCustomer.phone}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, phone: e.target.value })
              }
              className="border p-2 rounded-md focus:outline-blue-500"
            />
            <input
              type="text"
              placeholder="Desired Phone Model"
              value={newCustomer.model}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, model: e.target.value })
              }
              className="border p-2 rounded-md focus:outline-blue-500"
            />
            <select
              value={newCustomer.priority || "medium"}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, priority: e.target.value })
              }
              className="border p-2 rounded-md focus:outline-blue-500"
            >
              <option value="high">High Priority</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <button
              onClick={addCustomer}
              className="bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700"
            >
              Add
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by name, phone, or model..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="border p-2 rounded-md w-64 focus:outline-blue-500"
          />
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="border p-2 rounded-md focus:outline-blue-500"
          >
            <option value="all">All</option>
            <option value="waiting">Waiting</option>
            <option value="called">Called</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white border rounded-xl shadow-md">
          {loading ? (
            <p className="p-4 text-center text-gray-500">Loading...</p>
          ) : error ? (
            <p className="p-4 text-center text-red-500">{error}</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 border-b text-left">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3">Model</th>
                  <th className="p-3">Priority</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Purchase</th> {/* ✅ New */}
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    className={`border-b ${
                      c.status === "called" ? "bg-green-50" : "bg-yellow-50"
                    }`}
                  >
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3">{c.phone}</td>
                    <td className="p-3">{c.model}</td>

                    {/* ✅ Priority with color */}
                    <td
                      className={`p-3 font-semibold ${
                        c.priority === "high"
                          ? "text-red-600"
                          : c.priority === "medium"
                          ? "text-yellow-600"
                          : "text-gray-500"
                      }`}
                    >
                      {c.priority}
                    </td>

                    {/* ✅ Status & Purchase */}
                    <td className="p-3 capitalize">{c.status}</td>
                    <td className="p-3 capitalize">
                      {c.purchase_status || "pending"}
                    </td>

                    {/* ✅ Action Buttons */}
                    <td className="p-3 text-center">
                      <div className="flex flex-wrap justify-center gap-2">
                        {/* --- Mark as Called --- */}
                        {c.status !== "called" && (
                          <button
                            onClick={() => markAsCalled(c.id)}
                            className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-xs"
                          >
                            📞 Mark as Called
                          </button>
                        )}

                        {/* --- Priority Controls --- */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => updatePriority(c.id, "high")}
                            className={`px-2 py-1 text-xs rounded ${
                              c.priority === "high"
                                ? "bg-red-600 text-white"
                                : "bg-red-100 text-red-600 hover:bg-red-200"
                            }`}
                          >
                            High
                          </button>
                          <button
                            onClick={() => updatePriority(c.id, "medium")}
                            className={`px-2 py-1 text-xs rounded ${
                              c.priority === "medium"
                                ? "bg-yellow-500 text-white"
                                : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                            }`}
                          >
                            Med
                          </button>
                          <button
                            onClick={() => updatePriority(c.id, "low")}
                            className={`px-2 py-1 text-xs rounded ${
                              c.priority === "low"
                                ? "bg-gray-500 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Low
                          </button>
                        </div>

                        {/* --- Purchase Status --- */}
                        <div className="flex gap-1">
                          {c.purchase_status !== "bought" && (
                            <button
                              onClick={() => updatePurchase(c.id, "bought")}
                              className="bg-blue-600 text-white px-2 py-1 rounded-md hover:bg-blue-700 text-xs"
                            >
                              💰 Bought
                            </button>
                          )}
                          {c.purchase_status !== "not_come" && (
                            <button
                              onClick={() => updatePurchase(c.id, "not_come")}
                              className="bg-red-600 text-white px-2 py-1 rounded-md hover:bg-red-700 text-xs"
                            >
                              🚫 Didn’t Come
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Summary  */}

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* 📊 Monthly Summary */}
        {summary && (
          <div className="bg-gray-50 border p-4 rounded-xl shadow-sm">
            <h2 className="text-lg font-semibold mb-2">
              Monthly Summary (
              {new Date(2025, selectedMonth - 1).toLocaleString("default", {
                month: "long",
              })}
              )
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="p-3 bg-white rounded shadow text-center">
                <p className="text-gray-500">Registered</p>
                <p className="text-xl font-bold">{summary.totalCustomers}</p>
              </div>
              <div className="p-3 bg-white rounded shadow text-center">
                <p className="text-gray-500">Called</p>
                <p className="text-xl font-bold text-blue-600">
                  {summary.calledCount}
                </p>
              </div>
              <div className="p-3 bg-white rounded shadow text-center">
                <p className="text-gray-500">Bought</p>
                <p className="text-xl font-bold text-green-600">
                  {summary.boughtCount}
                </p>
              </div>
              <div className="p-3 bg-white rounded shadow text-center">
                <p className="text-gray-500">Didn’t Come</p>
                <p className="text-xl font-bold text-red-600">
                  {summary.notComeCount}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="font-semibold text-gray-700 mb-1">
                🔥 Most Requested Models
              </h3>
              {summary.topModels?.length ? (
                <ul className="list-disc ml-5 text-sm text-gray-700">
                  {summary.topModels.map((m) => (
                    <li key={m.model}>
                      {m.model} — <strong>{m.count}</strong> requests
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">No data yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
